import { useRef, useState } from "react";
import { ImagePlus, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { api, errorMessage, imageSrc } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Nedskalerer store bilete i nettlesaren (same tilnærming som arrangements- og varebilete)
const compressImage = (file, maxDim = 1920, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("komprimering feilet"))), "image/jpeg", quality);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("kunne ikke lese bildet"));
    };
    img.src = url;
  });

/** Lastar opp eit bilete til Right In (`POST /api/my/images`) og returnerer URL-en. Komprimerer ved behov. */
export async function uploadImage(file) {
  let payload = file;
  if (file.size > MAX_IMAGE_BYTES) {
    const blob = await compressImage(file);
    if (blob.size > MAX_IMAGE_BYTES) throw new Error("for stort");
    payload = new File([blob], (file.name.replace(/\.[^.]+$/, "") || "bilde") + ".jpg", { type: "image/jpeg" });
  }
  const fd = new FormData();
  fd.append("file", payload);
  const { data } = await api.post("/my/images", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

/**
 * Biletfelt med opplasting frå eiga eining (brukarval): førehandsvising + «Last opp / Bytt / Fjern».
 * `value` = bilete-URL, `onChange(url)`. `aspect` = tailwind aspect-klasse for førehandsvisinga.
 */
export const ImageUploadField = ({ value, onChange, aspect = "aspect-[4/3]", size = "w-full sm:w-56", testId = "image-upload", hint, disabled = false, alt = "" }) => {
  const { t } = useLang();
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const pick = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadImage(file));
      toast.success(t("Bildet er lastet opp"));
    } catch (err) {
      toast.error(err?.response ? errorMessage(err, t("Kunne ikke laste opp bildet")) : t("Kunne ikke laste opp bildet"));
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-4" data-testid={testId}>
      <div className={`${size} ${aspect} rounded-xl overflow-hidden bg-muted border border-border shrink-0`}>
        {value ? <img src={imageSrc(value)} alt={alt} className="h-full w-full object-cover" data-testid={`${testId}-preview`} /> : <div className="h-full w-full flex items-center justify-center text-muted-foreground"><ImagePlus className="w-8 h-8" /></div>}
      </div>
      <div className="space-y-2 text-sm">
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => pick(e.target.files?.[0])} disabled={disabled} data-testid={`${testId}-input`} />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => ref.current?.click()} disabled={uploading || disabled} data-testid={`${testId}-button`}>
            {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} {uploading ? t("Laster opp …") : value ? t("Bytt bilde") : t("Last opp bilde")}
          </Button>
          {value && !disabled && <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testId}-remove`}><X className="w-4 h-4 mr-1.5" /> {t("Fjern bilde")}</Button>}
        </div>
        <p className="text-xs text-muted-foreground">{hint || t("JPG, PNG eller WEBP, maks 5 MB.")}</p>
      </div>
    </div>
  );
};
